import throttle from 'lodash/throttle';
import { useEffect } from 'react';

import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import type { Step } from '@atlaskit/editor-prosemirror/transform';
import { getDocBeforeUnconfirmedSteps, sendableSteps } from '@atlaskit/prosemirror-collab';
import { type Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { usePageContentId } from '@confluence/page-context';

import type { EditorOfflineStoragePlugin } from './editorOfflineStoragePluginType';
import { StepsDatabase } from './stepsDatabase';
import { useBroadcastChannel } from './broadcastChannel';
import { createPlugin, editorOfflineStoragePluginKey } from './pm-plugins/main';
import { restoreStepsAction } from './pm-plugins/actions';
export const editorOfflineStoragePlugin: EditorOfflineStoragePlugin = ({ api }) => {
	const stepsDatabase = new StepsDatabase(api);

	return {
		name: 'editorOfflineStorage',
		pmPlugins() {
			return [
				{
					name: 'editorOfflineStoragePlugin',
					plugin: createPlugin(api, stepsDatabase),
				},
			];
		},
		getSharedState(state) {
			if (!state) {
				return undefined;
			}
			const { status, retryCount, healthy } = editorOfflineStoragePluginKey.getState(state) ?? {};
			const { version, steps } = sendableSteps(state) ?? {};

			return {
				version: version ?? 0,
				steps: steps ?? [],
				status: status ?? 'pending',
				retryCount: retryCount ?? 0,
				healthy: healthy ?? true,
			};
		},
		usePluginHook: ({ editorView }) => {
			const [contentId] = usePageContentId();
			useEffect(() => {
				if (contentId) {
					void stepsDatabase.init(contentId);
				}
			}, [contentId]);

			const broadcastUpdate = useBroadcastChannel({ api, editorView, stepsDatabase });

			usePluginStateEffect(
				api,
				['editorOfflineStorage', 'connectivity', 'collabEdit'],
				({ editorOfflineStorageState, connectivityState, collabEditState }) => {
					const { status, steps, version } = editorOfflineStorageState ?? {};

					if (window?.indexedDB === undefined) {
						return;
					}

					if (steps?.length === 0 && stepsDatabase.hasLock) {
						void stepsDatabase.clear(false).then((cleared) => {
							if (cleared) {
								stepsDatabase.releaseSyncLock?.();
							}
						});
					}

					switch (status) {
						case 'pending':
							// Restore steps once collab edit plugin is initialised
							if (collabEditState?.initialised.collabInitialisedAt) {
								const tr: Transaction = editorView.state.tr;
								tr.setMeta('addToHistory', false);
								tr.setMeta(editorOfflineStoragePluginKey, {
									status: 'restoring',
								});
								editorView.dispatch(tr);
								void restoreStepsAction(api, stepsDatabase)(editorView.state.schema);
							}
							break;

						case 'restoring':
							// Do nothing until the steps are restored
							break;

						case 'ready':
							const isFocusedAndVisible =
								document.visibilityState === 'visible' && document.hasFocus();
							// If we are offline and we have steps to store, update the steps in the database
							if (
								isFocusedAndVisible &&
								steps &&
								version !== undefined &&
								connectivityState?.mode === 'offline'
							) {
								void syncWithOfflineStorage({
									editorView,
									stepsDatabase,
									version,
									steps,
									broadcast: broadcastUpdate,
								});
							}
							break;

						case 'tooManyRetries':
							// Do nothing, unable to restore steps
							break;

						default:
							break;
					}
				},
			);
		},
	};
};

type StoreUnconfirmedStepsProps = {
	editorView: EditorView;
	stepsDatabase: StepsDatabase;
	version: number;
	steps: readonly Step[];
	broadcast: () => void;
};

const syncWithOfflineStorage = throttle(
	async ({ editorView, stepsDatabase, version, steps, broadcast }: StoreUnconfirmedStepsProps) => {
		const baseDocJSON = getDocBeforeUnconfirmedSteps(editorView.state).toJSON();
		const hasChanged = await stepsDatabase.updateIfChanged(version, baseDocJSON, steps);
		if (hasChanged) {
			broadcast();
		}
	},
	200,
);
